import { useEffect, useRef, useState, useMemo } from "react";
import * as echarts from "echarts";
import type { EChartsOption, ECharts } from "echarts";
import type { ZoomState, DataZoomEvent, OpenInterestDataPoint } from "@/types/coinglass";
import { useTokenDerivatives } from "@/contexts/token";
import { aggregateDataOpenInterest, getAggregationLevel2 } from "@/utils/coinglass";
import { buildGrid, buildTimeAxis, buildValueAxis, buildInsideZoom } from "@/utils/charts/options";
import { formatPrice, formatUsdBillions } from "@/utils/charts/formatters";
import { formatDateLabel } from "@/utils/charts/dataUtils";
import { ChartSkeleton } from "@/components/charts";

interface TokenOpenInterestChartProps {
  tokenId: string;
  className?: string;
  hideTitle?: boolean;
  containerClassName?: string;
  externalRangeDays?: number | null;
}

function TokenOpenInterestChart({
  tokenId,
  className = "",
  hideTitle = false,
  containerClassName,
  externalRangeDays,
}: TokenOpenInterestChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<ECharts | null>(null);
  const rawDataRef = useRef<OpenInterestDataPoint[]>([]);
  const isUpdatingRef = useRef<boolean>(false);
  const lastZoomRef = useRef<ZoomState>({ start: 0, end: 100, aggregation: 1 });

  const [chartReady, setChartReady] = useState(false);
  
  // Consume data from context
  const { openInterest, isLoading, isError, error } = useTokenDerivatives();
  
  // Convert context series to chart format
  const chartData = useMemo<OpenInterestDataPoint[]>(() => {
    return openInterest.series.map(point => ({
      timestamp: point.timestamp,
      openInterest: point.openInterest,
      price: point.price ?? null,
    }));
  }, [openInterest.series]);

  // Update raw data ref when data changes
  useEffect(() => {
    if (chartData.length > 0) {
      rawDataRef.current = chartData;
      setChartReady(true);
    }
  }, [chartData]);

  // Initialize chart
  useEffect(() => {
    if (isLoading || !chartReady || !chartRef.current || rawDataRef.current.length === 0) {
      return;
    }

    const chart = echarts.init(chartRef.current);
    chartInstanceRef.current = chart;

    const totalPoints = rawDataRef.current.length;
    const desiredDays = externalRangeDays ?? 180;
    const startPercentage = Math.max(0, ((totalPoints - desiredDays) / totalPoints) * 100);
    const initialAggregation = getAggregationLevel2(totalPoints, 100 - startPercentage);

    const aggregatedData = aggregateDataOpenInterest(rawDataRef.current, initialAggregation);

    const pricesSeries = aggregatedData.map((d) => [d.timestamp, d.price] as [number, number | null]);
    const oiSeries = aggregatedData.map((d) => [d.timestamp, d.openInterest] as [number, number]);

    const option = {
      backgroundColor: "transparent",
      legend: {
        data: ["Open Interest", `${tokenId} Price`],
        top: "2%",
        left: "center",
        textStyle: { color: "#9ca3af", fontSize: 12 },
        itemWidth: 25,
        itemHeight: 14,
        icon: "roundRect",
      },
      grid: buildGrid({ top: 40, right: 24, bottom: 24, left: 16, containLabel: true }),
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross", crossStyle: { color: "#999" } },
        backgroundColor: "rgba(17, 24, 39, 0.95)",
        borderColor: "#374151",
        borderWidth: 1,
        textStyle: { color: "#f3f4f6" },
        formatter: (params: unknown) => {
          if (!Array.isArray(params) || params.length === 0) return "";
          const first = params[0] as { value: unknown };
          const raw = (first?.value ?? null) as unknown;
          const ts = Array.isArray(raw) ? Number(raw[0]) : Number((params[0] as { axisValue: number | string }).axisValue);
          const dateStr = Number.isFinite(ts)
            ? formatDateLabel(ts, "medium")
            : String((params[0] as { axisValue: unknown }).axisValue ?? "");
          let result = `<div style="font-size:11px;font-weight:500;margin-bottom:8px;color:#9ca3af;">${dateStr}</div>`;

          params.forEach((param: unknown) => {
            const p = param as { seriesName: string; value: unknown; color: string };
            const val = Array.isArray(p.value) ? Number(p.value[1]) : Number(p.value as number);
            let formattedValue = "";
            if (p.seriesName === "Open Interest") {
              formattedValue = Number.isFinite(val) ? `$${(val / 1e9).toFixed(2)}B` : "N/A";
            } else {
              formattedValue = Number.isFinite(val)
                ? `$${val.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : "N/A";
            }
            result += `<div style="margin: 4px 0;">
              <span style="display: inline-block; width: 10px; height: 10px; background-color: ${p.color}; border-radius: 50%; margin-right: 8px;"></span>
              <span style="font-weight: 500; color: #d1d5db;">${p.seriesName}:</span> <span style="color: #f3f4f6;">${formattedValue}</span>
            </div>`;
          });

          return result;
        },
      },
      xAxis: buildTimeAxis(),
      yAxis: [
        buildValueAxis("Open Interest", { position: "left", formatter: (v) => formatUsdBillions(typeof v === 'string' ? parseFloat(v) : v) }),
        { ...buildValueAxis("Price", { position: "right", formatter: (v) => formatPrice(typeof v === 'string' ? parseFloat(v) : v) }), splitLine: { show: false } },
      ],
      dataZoom: [{ ...buildInsideZoom()[0], start: startPercentage, end: 100 }],
      series: [
        {
          name: "Open Interest",
          type: "line",
          yAxisIndex: 0,
          data: oiSeries,
          smooth: false,
          symbol: "none",
          lineStyle: { width: 0 },
          itemStyle: { color: "#10b981" },
          areaStyle: {
            color: {
              type: "linear",
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(16, 185, 129, 0.7)" },
                { offset: 1, color: "rgba(16, 185, 129, 0.1)" },
              ],
            },
          },
        },
        {
          name: `${tokenId} Price`,
          type: "line",
          yAxisIndex: 1,
          data: pricesSeries,
          smooth: false,
          symbol: "none",
          lineStyle: { color: "#fbbf24", width: 2 },
          itemStyle: { color: "#fbbf24" },
        },
      ],
    } as EChartsOption;

    chart.setOption(option);
    lastZoomRef.current.aggregation = initialAggregation;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const handleDataZoom = (event: unknown) => {
      if (isUpdatingRef.current) return;

      const zoomEvent = event as DataZoomEvent;
      const start = zoomEvent.start ?? zoomEvent.batch?.[0]?.start ?? 0;
      const end = zoomEvent.end ?? zoomEvent.batch?.[0]?.end ?? 100;

      if (start === lastZoomRef.current.start && end === lastZoomRef.current.end) {
        return;
      }

      lastZoomRef.current = { start, end, aggregation: lastZoomRef.current.aggregation };

      if (debounceTimer) clearTimeout(debounceTimer);

      debounceTimer = setTimeout(() => {
        const visiblePercentage = end - start;
        const newAggregation = getAggregationLevel2(rawDataRef.current.length, visiblePercentage);

        if (newAggregation === lastZoomRef.current.aggregation) return;

        lastZoomRef.current.aggregation = newAggregation;

        const newAggregatedData = aggregateDataOpenInterest(rawDataRef.current, newAggregation);
        const newPrices = newAggregatedData.map((d) => [d.timestamp, d.price] as [number, number | null]);
        const newOIs = newAggregatedData.map((d) => [d.timestamp, d.openInterest] as [number, number]);

        isUpdatingRef.current = true;
        chart.setOption({ series: [{ data: newOIs }, { data: newPrices }] }, { notMerge: false });
        setTimeout(() => { isUpdatingRef.current = false; }, 100);
      }, 250);
    };

    chart.on("dataZoom", handleDataZoom);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      window.removeEventListener("resize", handleResize);
      chart.off("dataZoom");
      chart.dispose();
    };
  }, [isLoading, chartReady, tokenId, externalRangeDays]);

  // Handle external range changes
  useEffect(() => {
    if (!chartInstanceRef.current || !rawDataRef.current.length) return;
    if (externalRangeDays == null) {
      isUpdatingRef.current = true;
      chartInstanceRef.current.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
      setTimeout(() => { isUpdatingRef.current = false; }, 100);
      return;
    }
    const totalPoints = rawDataRef.current.length;
    const start = Math.max(0, 100 - (externalRangeDays / totalPoints) * 100);
    isUpdatingRef.current = true;
    chartInstanceRef.current.dispatchAction({ type: 'dataZoom', start, end: 100 });
    setTimeout(() => { isUpdatingRef.current = false; }, 100);
  }, [externalRangeDays]);

  if (isLoading) {
    return <ChartSkeleton height={360} />;
  }

  if (isError) {
    return (
      <div className="flex h-[360px] items-center justify-center rounded-xl bg-gray-800/40 p-4">
        <div className="text-center">
          <p className="mb-2 text-xl font-semibold text-red-400">Error Loading Data</p>
          <p className="text-gray-400">{error?.message || "Failed to load open interest data"}</p>
        </div>
      </div>
    );
  }

  if (!chartReady || chartData.length === 0) {
    return <ChartSkeleton height={360} />;
  }

  return (
    <div className={className}>
      {!hideTitle && (
        <h2 className="mb-4 text-center text-2xl font-bold text-white">
          {tokenId.toUpperCase()} Open Interest
        </h2>
      )}
      <div ref={chartRef} className={containerClassName ?? "h-[500px] w-full rounded-lg bg-gray-900 p-4"} />
    </div>
  );
}

export default TokenOpenInterestChart;
